import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    Image,
} from 'react-native';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useOrganizationData, useOrganizationOffers, useAppData} from "@/data/app-data-hooks";
import {ThemeType} from "@/theming/baseTheme";
import {useTranslation} from "react-i18next";
import {useOrganizationNavigation} from "@/hooks/useOrganizationNavigation";
import {HeaderText} from "@/components/common/HeaderText";
import {useAuth} from "@/auth/useAuth";
import {useClearLastSelectedOrg} from "@/data/user-data-hooks";
import {router} from "expo-router";

interface MembershipProfileProps {
    orgId: string;
}

export const MembershipProfile = ({ orgId }: MembershipProfileProps) => {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const {user} = useAuth();
    const styles = createStyles(theme);
    const {data: organizationData} = useOrganizationData(orgId);
    const {data: offers} = useOrganizationOffers(orgId);
    const {navigateToAchievements, navigateToMembershipPlan, navigateToOffers, navigateBack} = useOrganizationNavigation(orgId);
    const {data: appData} = useAppData();
    const clearLastSelectedOrg = useClearLastSelectedOrg();
    const hasMultipleOrgs = Object.keys(appData).length > 1;

    if (!organizationData) {
        return (
            <View style={styles.container}>
                <Text style={styles.loadingText}>{t('common.loading')}</Text>
            </View>
        );
    }

    const { organization, membership, achievements } = organizationData;

    const unlockedAchievements = achievements.filter(a => a.assigned);

    // Sort achievements: unlocked first (by date descending), then locked
    const allAchievements = [...achievements].sort((a, b) => {
        // If both are assigned, sort by date (most recent first)
        if (a.assigned && b.assigned) {
            return new Date(b.assigned).getTime() - new Date(a.assigned).getTime();
        }
        // If only one is assigned, it comes first
        if (a.assigned && !b.assigned) return -1;
        if (!a.assigned && b.assigned) return 1;
        // If neither is assigned, maintain original order
        return 0;
    });

    const handlePassPress = () => {
        console.log('Membership pass pressed');
    };

    const handleAchievementPress = (achievement: any) => {
        if (achievement.assigned) {
            console.log(`Achievement pressed: ${achievement.name}`);
        }
    };

    const handleSwitchOrg = () => {
        clearLastSelectedOrg.mutate(undefined, {
            onSuccess: () => {
                router.replace('/');
            }
        });
    };

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const renderOrgLogo = () => {
        if (organization.icon) {
            return (
                <Image
                    source={{ uri: organization.icon }}
                    style={styles.logoImage}
                    resizeMode="contain"
                />
            );
        }
        return (
            <Text style={styles.logoText}>
                {getInitials(organization.name)}
            </Text>
        );
    };

    return (
        <View style={styles.safeArea}>
            <HeaderText
                title={t('common.profile')}
                onBackPress={navigateBack}
            />
            <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
                {/* Membership Pass Card */}
                <TouchableOpacity style={styles.membershipPass} onPress={handlePassPress}>
                    <View style={styles.passHeader}>
                        <View style={styles.orgLogo}>
                            {renderOrgLogo()}
                        </View>
                        <View style={styles.membershipType}>
                            <Text style={styles.membershipTypeText}>
                                {membership.name.toUpperCase()}
                            </Text>
                        </View>
                    </View>

                    <View style={styles.memberInfo}>
                        <Text style={styles.memberName}>{user?.firstname} {user?.lastname}</Text>
                        <Text style={styles.orgName}>{organization.name}</Text>
                    </View>

                    <View style={styles.passFooter}>
                        <Text style={styles.memberId}>{t('profile.idPrefix')}{organization.id.slice(0, 8).toUpperCase()}</Text>
                        <View style={styles.recentAchievement}>
                            {unlockedAchievements.length > 0 && (
                                <Image
                                    source={{ uri: unlockedAchievements[unlockedAchievements.length - 1].icon }}
                                    style={styles.recentAchievementImage}
                                    resizeMode="cover"
                                />
                            )}
                        </View>
                    </View>
                </TouchableOpacity>

                {/* Achievements Section */}
                <View style={styles.achievementsSection}>
                    <View style={styles.achievementsHeader}>
                        <Text style={styles.achievementsTitle}>{t('achievements.title')}</Text>
                        <TouchableOpacity onPress={navigateToAchievements}>
                            <Text style={styles.viewAllBtn}>
                                {t('dashboard.sections.viewAllAction')} ({allAchievements.length})
                            </Text>
                        </TouchableOpacity>
                    </View>

                    <View style={styles.achievementsGrid}>
                        {allAchievements.slice(0, allAchievements.length >= 8 ? 8 : 4).map((achievement, index) => {
                            const isLocked = !achievement.assigned;
                            return (
                                <View key={achievement.id} style={styles.achievementWrapper}>
                                    <TouchableOpacity
                                        style={[
                                            styles.achievementBadge,
                                            isLocked && styles.achievementBadgeLocked,
                                        ]}
                                        onPress={() => handleAchievementPress(achievement)}
                                        disabled={isLocked}
                                    >
                                        <View style={styles.achievementIconContainer}>
                                            <Image
                                                source={{ uri: achievement.icon }}
                                                style={[
                                                    styles.achievementIcon,
                                                    isLocked && styles.achievementIconLocked,
                                                ]}
                                                resizeMode="contain"
                                            />
                                        </View>
                                        <View style={styles.achievementTextContainer}>
                                            <Text
                                                style={[
                                                    styles.achievementLabel,
                                                    isLocked && styles.achievementLabelLocked,
                                                ]}
                                                numberOfLines={1}
                                                ellipsizeMode="tail"
                                            >
                                                {achievement.name}
                                            </Text>
                                        </View>
                                    </TouchableOpacity>
                                </View>
                            );
                        })}
                    </View>
                </View>

                {/* My Purchases Section */}
                {offers && offers.filter(o => o.isPurchased).length > 0 && (
                    <View style={styles.purchasesSection}>
                        <View style={styles.purchasesHeader}>
                            <Text style={styles.purchasesTitle}>{t('profile.myPurchases')}</Text>
                            <TouchableOpacity onPress={navigateToOffers}>
                                <Text style={styles.viewAllBtn}>
                                    {t('dashboard.sections.viewAllAction')} ({offers.filter(o => o.isPurchased).length})
                                </Text>
                            </TouchableOpacity>
                        </View>

                        <View style={styles.purchasesList}>
                            {offers.filter(o => o.isPurchased).slice(0, 3).map((offer) => (
                                <TouchableOpacity
                                    key={offer.id}
                                    style={styles.purchaseItem}
                                    onPress={navigateToOffers}
                                >
                                    <View style={styles.purchaseInfo}>
                                        <Text style={styles.purchaseTitle} numberOfLines={1}>
                                            {offer.title}
                                        </Text>
                                        <Text style={styles.purchaseDescription} numberOfLines={2}>
                                            {offer.description}
                                        </Text>
                                    </View>
                                    <View style={styles.purchaseBadge}>
                                        <Text style={styles.purchaseBadgeText}>✓</Text>
                                    </View>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>
                )}

                {/* Membership Plan Link */}
                <TouchableOpacity
                    style={styles.membershipPlanButton}
                    onPress={navigateToMembershipPlan}
                    activeOpacity={0.7}
                >
                    <View style={styles.membershipPlanContent}>
                        <View>
                            <Text style={styles.membershipPlanTitle}>{t('membership.title')}</Text>
                            <Text style={styles.membershipPlanSubtitle}>
                                {t('membership.management.title')}
                            </Text>
                        </View>
                        <Text style={styles.membershipPlanArrow}>›</Text>
                    </View>
                </TouchableOpacity>

                {/* Switch Organization */}
                {hasMultipleOrgs && (
                    <TouchableOpacity
                        style={styles.membershipPlanButton}
                        onPress={handleSwitchOrg}
                        activeOpacity={0.7}
                    >
                        <View style={styles.membershipPlanContent}>
                            <View>
                                <Text style={styles.membershipPlanTitle}>{t('orgSelection.switchOrg')}</Text>
                                <Text style={styles.membershipPlanSubtitle}>
                                    {t('orgSelection.switchOrgSubtitle')}
                                </Text>
                            </View>
                            <Text style={styles.membershipPlanArrow}>›</Text>
                        </View>
                    </TouchableOpacity>
                )}

                <View style={styles.bottomSpacing} />
            </ScrollView>
        </View>
    );
};

const createStyles = (theme: ThemeType) =>
    StyleSheet.create({
        safeArea: {
            flex: 1,
            backgroundColor: theme.colors.background,
        },
        container: {
            flex: 1,
        },
        loadingText: {
            color: theme.colors.text,
            textAlign: 'center',
            marginTop: 50,
            fontSize: theme.fontSize.md,
        },
        membershipPass: {
            margin: theme.spacing.md,
            height: 220,
            backgroundColor: theme.colors.primary,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.md,
            shadowColor: theme.colors.primary,
            shadowOffset: {
                width: 0,
                height: 8,
            },
            shadowOpacity: 0.3,
            shadowRadius: 10,
            elevation: 8,
        },
        passHeader: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: theme.spacing.md,
        },
        orgLogo: {
            width: 50,
            height: 50,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: theme.borderRadius.sm,
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing.xs,
        },
        logoImage: {
            width: '100%',
            height: '100%',
        },
        logoText: {
            fontSize: theme.fontSize.lg,
            fontWeight: 'bold',
            color: '#000',
        },
        membershipType: {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            paddingVertical: 6,
            paddingHorizontal: 12,
            borderRadius: 20,
        },
        membershipTypeText: {
            fontSize: theme.fontSize.xs,
            fontWeight: '700',
            color: '#000',
        },
        memberInfo: {
            marginBottom: theme.spacing.lg,
        },
        memberName: {
            fontSize: theme.fontSize.xl,
            fontWeight: '700',
            color: '#000',
            marginBottom: 4,
        },
        orgName: {
            fontSize: theme.fontSize.sm,
            color: 'rgba(0, 0, 0, 0.7)',
            fontWeight: '600',
        },
        passFooter: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 'auto',
        },
        memberId: {
            fontSize: theme.fontSize.xs,
            color: 'rgba(0, 0, 0, 0.6)',
            fontWeight: '600',
        },
        recentAchievement: {
            width: 40,
            height: 40,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: 4,
            overflow: 'hidden',
            padding: 4,
        },
        recentAchievementImage: {
            width: '100%',
            height: '100%',
            borderRadius: 2,
        },
        achievementsSection: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.md,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        achievementsHeader: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing.lg,
        },
        achievementsTitle: {
            fontSize: theme.fontSize.lg,
            fontWeight: '700',
            color: theme.colors.text,
        },
        viewAllBtn: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.primary,
            fontWeight: '600',
        },
        achievementsGrid: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            marginHorizontal: -theme.spacing.xs / 2,
        },
        achievementWrapper: {
            width: '25%',
            paddingHorizontal: theme.spacing.xs / 2,
            marginBottom: theme.spacing.xs,
        },
        achievementBadge: {
            height: 100,
            borderColor: theme.colors.secondary,
            borderWidth: 1,
            borderRadius: theme.borderRadius.md,
            overflow: 'hidden',
            flexDirection: 'column',
        },
        achievementBadgeLocked: {
            backgroundColor: theme.colors.border,
            opacity: 0.5,
        },
        achievementIconContainer: {
            flex: 1,
            padding: theme.spacing.sm,
            alignItems: 'center',
            justifyContent: 'center',
        },
        achievementIcon: {
            width: '100%',
            height: '100%',
        },
        achievementIconLocked: {
            opacity: 0.3,
        },
        achievementTextContainer: {
            borderTopWidth:1,
            borderTopColor: theme.colors.secondary,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            paddingVertical: 6,
            paddingHorizontal: 4,
            alignItems: 'center',
            justifyContent: 'center',
        },
        achievementLabel: {
            fontSize: 10,
            fontWeight: '600',
            textAlign: 'center',
            color: '#FFFFFF',
        },
        achievementLabelLocked: {
            color: theme.colors.textSecondary,
        },
        purchasesSection: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.md,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        purchasesHeader: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing.lg,
        },
        purchasesTitle: {
            fontSize: theme.fontSize.lg,
            fontWeight: '700',
            color: theme.colors.text,
        },
        purchasesList: {
            gap: theme.spacing.sm,
        },
        purchaseItem: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: theme.spacing.sm,
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.border,
        },
        purchaseInfo: {
            flex: 1,
            marginRight: theme.spacing.sm,
        },
        purchaseTitle: {
            fontSize: theme.fontSize.md,
            fontWeight: '600',
            color: theme.colors.text,
            marginBottom: theme.spacing.xs,
        },
        purchaseDescription: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
            lineHeight: 18,
        },
        purchaseBadge: {
            width: 28,
            height: 28,
            borderRadius: 14,
            backgroundColor: theme.colors.primary,
            alignItems: 'center',
            justifyContent: 'center',
        },
        purchaseBadgeText: {
            color: '#FFFFFF',
            fontSize: 16,
            fontWeight: 'bold',
        },
        membershipPlanButton: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        membershipPlanContent: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        membershipPlanTitle: {
            fontSize: theme.fontSize.lg,
            fontWeight: '700',
            color: theme.colors.text,
            marginBottom: theme.spacing.xs,
        },
        membershipPlanSubtitle: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
        },
        membershipPlanArrow: {
            fontSize: 24,
            color: theme.colors.textSecondary,
            fontWeight: '300',
        },
        bottomSpacing: {
            height: theme.spacing.xl,
        },
    });
